import React, { useState, useEffect } from 'react';
import {
  ComposedChart, Line, Area, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine, ReferenceArea
} from 'recharts';
import { TrendingUp, TrendingDown, Activity } from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';

interface ForecastData {
  date: string;
  actual?: number;
  forecast: number;
  lower: number;
  upper: number;
}

interface ForecastChartProps {
  data: ForecastData[];
}

const ForecastChart: React.FC<ForecastChartProps> = ({ data: initialData }) => {
  const [chartData, setChartData] = useState<any[]>([]);
  const [viewType, setViewType] = useState<'composed' | 'line' | 'area'>('composed');
  const [showConfidenceInterval, setShowConfidenceInterval] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  
  useEffect(() => {
    // データを変換
    const transformedData = initialData.map(d => ({
      日付: new Date(d.date).toLocaleDateString('ja-JP', { 
        month: 'short', 
        day: 'numeric' 
      }),
      実績: d.actual || null,
      予測: d.forecast,
      下限: d.lower,
      上限: d.upper,
      信頼区間: d.upper - d.lower
    }));
    
    setChartData(transformedData);
    
    // アニメーション効果
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 1500);
  }, [initialData]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border" style={{ borderColor: colors.ui.border }}>
          <p className="font-semibold text-sm mb-2" style={{ color: colors.text.primary }}>
            {label}
          </p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center justify-between gap-4 text-xs">
              <span style={{ color: entry.color }}>
                {entry.name}:
              </span>
              <span className="font-medium" style={{ color: colors.text.primary }}>
                ¥{entry.value ? entry.value.toLocaleString() : '-'}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const getGradientDefs = () => (
    <defs>
      <linearGradient id="colorForecast" x1="0" y1="0" x2="0" y2="1">
        <stop offset="5%" stopColor={colors.accent.blue} stopOpacity={0.3}/>
        <stop offset="95%" stopColor={colors.accent.blue} stopOpacity={0.05}/>
      </linearGradient>
      <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
        <stop offset="5%" stopColor={colors.ui.success} stopOpacity={0.3}/>
        <stop offset="95%" stopColor={colors.ui.success} stopOpacity={0.05}/>
      </linearGradient>
      <linearGradient id="colorConfidence" x1="0" y1="0" x2="0" y2="1">
        <stop offset="5%" stopColor={colors.accent.cyan} stopOpacity={0.15}/>
        <stop offset="95%" stopColor={colors.accent.cyan} stopOpacity={0.02}/>
      </linearGradient>
    </defs>
  );

  const renderChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 10, right: 10, left: 0, bottom: 0 }
    };

    if (viewType === 'line') {
      return (
        <ComposedChart {...commonProps}>
          {getGradientDefs()}
          <CartesianGrid strokeDasharray="3 3" stroke={colors.ui.border} />
          <XAxis 
            dataKey="日付" 
            stroke={colors.text.secondary}
            fontSize={10}
          />
          <YAxis 
            stroke={colors.text.secondary}
            fontSize={10}
            tickFormatter={(value) => `¥${(value / 1000).toFixed(0)}k`}
          />
          <Tooltip content={<CustomTooltip />} />
          
          <Line
            type="monotone"
            dataKey="実績"
            stroke={colors.ui.success}
            strokeWidth={2}
            dot={{ fill: colors.ui.success, r: 3 }}
            activeDot={{ r: 5 }}
            animationDuration={isAnimating ? 1500 : 0}
          />
          <Line
            type="monotone"
            dataKey="予測"
            stroke={colors.accent.blue}
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ fill: colors.accent.blue, r: 2 }}
            animationDuration={isAnimating ? 1500 : 0}
          />
        </ComposedChart>
      );
    }

    if (viewType === 'area') {
      return (
        <ComposedChart {...commonProps}>
          {getGradientDefs()}
          <CartesianGrid strokeDasharray="3 3" stroke={colors.ui.border} />
          <XAxis 
            dataKey="日付" 
            stroke={colors.text.secondary}
            fontSize={10}
          />
          <YAxis 
            stroke={colors.text.secondary}
            fontSize={10}
            tickFormatter={(value) => `¥${(value / 1000).toFixed(0)}k`}
          />
          <Tooltip content={<CustomTooltip />} />
          
          <Area
            type="monotone"
            dataKey="予測"
            stroke={colors.accent.blue}
            fill="url(#colorForecast)"
            strokeWidth={2}
            animationDuration={isAnimating ? 1500 : 0}
          />
          <Area
            type="monotone"
            dataKey="実績"
            stroke={colors.ui.success}
            fill="url(#colorActual)"
            strokeWidth={2}
            animationDuration={isAnimating ? 1500 : 0}
          />
        </ComposedChart>
      );
    }

    // Composed (デフォルト)
    return (
      <ComposedChart {...commonProps}>
        {getGradientDefs()}
        <CartesianGrid strokeDasharray="3 3" stroke={colors.ui.border} />
        <XAxis 
          dataKey="日付" 
          stroke={colors.text.secondary}
          fontSize={10}
        />
        <YAxis 
          stroke={colors.text.secondary}
          fontSize={10}
          tickFormatter={(value) => `¥${(value / 1000).toFixed(0)}k`}
        />
        <Tooltip content={<CustomTooltip />} />
        
        {showConfidenceInterval && (
          <Area
            type="monotone"
            dataKey="信頼区間"
            stackId="1"
            stroke="none"
            fill="url(#colorConfidence)"
            animationDuration={isAnimating ? 1200 : 0}
          />
        )}
        
        <Bar
          dataKey="実績"
          fill={colors.ui.success}
          opacity={0.6}
          animationDuration={isAnimating ? 1000 : 0}
        />
        
        <Line
          type="monotone"
          dataKey="予測"
          stroke={colors.accent.blue}
          strokeWidth={2.5}
          dot={{ fill: colors.accent.blue, r: 3 }}
          activeDot={{ r: 5 }}
          strokeDasharray="5 5"
          animationDuration={isAnimating ? 1500 : 0}
        />
        
        {showConfidenceInterval && (
          <>
            <Line
              type="monotone"
              dataKey="上限"
              stroke={colors.accent.cyan}
              strokeWidth={1}
              strokeDasharray="3 3"
              dot={false}
              opacity={0.5}
              animationDuration={isAnimating ? 1200 : 0}
            />
            <Line
              type="monotone"
              dataKey="下限"
              stroke={colors.accent.cyan}
              strokeWidth={1}
              strokeDasharray="3 3"
              dot={false}
              opacity={0.5}
              animationDuration={isAnimating ? 1200 : 0}
            />
          </>
        )}
      </ComposedChart>
    );
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold" style={{ color: colors.text.primary }}>
          需要予測トレンド
        </h3>
        <div className="flex items-center gap-2">
          <Button
            size="tiny"
            variant={viewType === 'composed' ? 'primary' : 'ghost'}
            onClick={() => setViewType('composed')}
          >
            複合
          </Button>
          <Button
            size="tiny"
            variant={viewType === 'line' ? 'primary' : 'ghost'}
            onClick={() => setViewType('line')}
          >
            折線
          </Button>
          <Button
            size="tiny"
            variant={viewType === 'area' ? 'primary' : 'ghost'}
            onClick={() => setViewType('area')}
          >
            面積
          </Button>
          <div className="w-px h-4 bg-gray-300" />
          <Button
            size="tiny"
            variant={showConfidenceInterval ? 'secondary' : 'ghost'}
            onClick={() => setShowConfidenceInterval(!showConfidenceInterval)}
          >
            信頼区間
          </Button>
        </div>
      </div>
      
      <div className="w-full" style={{ height: '250px' }}>
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
      
      {/* 凡例と統計情報 */}
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-3 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-0.5" style={{ backgroundColor: colors.accent.blue }} />
            <span style={{ color: colors.text.secondary }}>予測値</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: colors.ui.success }} />
            <span style={{ color: colors.text.secondary }}>実績値</span>
          </div>
          {showConfidenceInterval && (
            <div className="flex items-center gap-1">
              <div className="w-3 h-2 rounded-sm" style={{ backgroundColor: colors.accent.cyan, opacity: 0.2 }} />
              <span style={{ color: colors.text.secondary }}>信頼区間</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {chartData.length > 0 && chartData[chartData.length - 1].予測 > chartData[0].予測 ? (
            <Badge variant="success" size="sm">
              <TrendingUp size={10} />
              上昇トレンド
            </Badge>
          ) : (
            <Badge variant="danger" size="sm">
              <TrendingDown size={10} />
              下降トレンド
            </Badge>
          )}
          <Badge variant="default" size="sm">
            <Activity size={10} />
            精度: 92.3%
          </Badge>
        </div>
      </div>
    </div>
  );
};

export default ForecastChart;